import {
  Autocomplete,
  Box,
  Checkbox,
  FormControlLabel,
  Grid,
  Tooltip,
  Typography,
} from "@mui/material";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";

import { ShiftDetails2 } from "../../orderAPI";
import { OrderFormShortRowGridItem } from "../../styles";
import {
  DesktopDatePickerLight,
  TextFieldLight,
} from "../../../../common/components/styled/styled";
import OrderTimeField from "../../OrderTimeField";
import { ShiftTalentInfo } from "./ShiftTalentInfo";
import { AutocompleteType } from "../../../../common/types/autoComplete";
import { AutocompleteOptions, ValidationError } from "../../orderTypes";
import {
  CancelAssignmentCallback,
  HandleCustomerCCChangeFunction,
  HandleFillShiftChangeFunction,
  HandleFlexibleStartTimeChangeFunction,
  HandleTimeChangeFunction,
  CancelAllAssignmentsCallback,
} from "../../types/editOrder";
import { useContext, useEffect, useState } from "react";
import OrderFlexibleStartField from "../../components/OrderFlexibleStartField";
import {
  hoursMinutesFormat,
  leadingZerosDateFormat,
  rawHoursMinutesFormat,
} from "../../../../common/helpers/formatDateTime";
import {
  formatFlexibleStartTime,
  getCustomerCostCenterName,
  validateFlexibleStartTime,
} from "../../utils";
import { ShiftOrderContext } from "../../newOrder/short/OrderShortDataProvider";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { ReactComponent as ClockIcon } from "../../../../common/icons/clock.svg";
import { useParams } from "react-router-dom";
import { useAppSelector } from "../../../../app/hooks";
import { selectOrderRowById } from "../../orderRowsDetailedSlice";

type Props = {
  isReadOnly: boolean;
  isAutomatchingOn: boolean;
  isExpertAuthorizedToEdit: boolean;
  row: ShiftDetails2;
  options: AutocompleteOptions;
  validationErrors: ValidationError[];
  handleTimeChange: HandleTimeChangeFunction;
  handleCustomerCCChange: HandleCustomerCCChangeFunction;
  handleFillShiftChange: HandleFillShiftChangeFunction;
  handleFlexibleStartTimeChange: HandleFlexibleStartTimeChangeFunction;
  cancelAssignment: CancelAssignmentCallback;
  isBundle: boolean;
  hasCancelledShiftByTalent: Map<string, boolean>;
  cancelAllAssignments: CancelAllAssignmentsCallback;
};

export const ShiftInfoRow = ({
  isReadOnly,
  isAutomatchingOn,
  isExpertAuthorizedToEdit,
  row,
  options,
  validationErrors,
  handleTimeChange,
  handleCustomerCCChange,
  handleFillShiftChange,
  handleFlexibleStartTimeChange,
  cancelAssignment,
  isBundle,
  hasCancelledShiftByTalent,
  cancelAllAssignments,
}: Props) => {
  const { t } = useTranslation("order");
  const { handleError } = useContext(ShiftOrderContext);
  const { bundleId } = useParams();
  const shiftDetails = useAppSelector((state) =>
    selectOrderRowById(state, bundleId as string),
  )?.shiftDetails;
  const talent = shiftDetails?.find(
    (sd) =>
      sd.id === row.id && sd.assignedTalent?.id === row.assignedTalent?.id,
  )?.assignedTalent;

  const hasCancelledForTalent =
    !!row.assignedTalent &&
    hasCancelledShiftByTalent.get(row.assignedTalent.id) === true;
  const canCancelAllShifts = isBundle && !hasCancelledForTalent;

  const startTime = format(
    new Date(row.shiftDetail.time.start),
    rawHoursMinutesFormat,
  );
  const endTime = format(
    new Date(row.shiftDetail.time.end),
    rawHoursMinutesFormat,
  );
  const shiftFlexibleStartTime = row.shiftDetail.flexibleStartUntil;
  const flexibleStartTimeValue = shiftFlexibleStartTime
    ? format(new Date(shiftFlexibleStartTime), rawHoursMinutesFormat)
    : "";
  const isFlexShiftDisabled =
    isReadOnly || isAutomatchingOn || !isExpertAuthorizedToEdit || !!talent;
  const talentAgreedFlexibleStartTime = row.assignedTalent?.agreedToStartShiftAt
    ? format(
        new Date(row.assignedTalent?.agreedToStartShiftAt),
        rawHoursMinutesFormat,
      )
    : undefined;

  const [isStartFlexible, setIsStartFlexible] = useState(
    !!shiftFlexibleStartTime,
  );
  const isFlexStartValid = validateFlexibleStartTime(
    flexibleStartTimeValue,
    startTime + endTime,
    isStartFlexible,
  );

  // get the selected custCC from the options first, because it might be
  // inactive, then filter the inactive ones out from the
  // options.
  const custCC = {
    id: row.shiftDetail.custCCId,
    name: getCustomerCostCenterName(
      options.costCenters,
      row.shiftDetail.custCCId,
    ),
  };

  const custCCOptions = options?.costCenters.filter(
    (cc) => cc.active,
  ) as AutocompleteType[];

  useEffect(() => {
    handleError(!isFlexStartValid.isValid, {
      id: "flexibleStartTime",
      field: "flexibleStartUntil",
      errorType: "",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFlexStartValid.isValid]);

  const time = talentAgreedFlexibleStartTime
    ? talentAgreedFlexibleStartTime + endTime
    : startTime + endTime;

  return (
    <Grid item container xs={12} direction="row" mt={1} key={row.id}>
      <Grid
        item
        xs={12}
        container
        spacing={1}
        sx={{ alignItems: "center", paddingLeft: "1rem" }}
      >
        <Grid item container xs={12} spacing={1}>
          <OrderFormShortRowGridItem item xs={2}>
            <Box display="flex" alignItems="center" gap="8px">
              {talentAgreedFlexibleStartTime && (
                <Tooltip
                  title={formatFlexibleStartTime(row, t)}
                  placement="bottom-start"
                >
                  <ClockIcon
                    width="18px"
                    height="18px"
                    style={{ flexShrink: 0, marginLeft: "2px" }}
                  />
                </Tooltip>
              )}
              <DesktopDatePickerLight
                disabled
                format={leadingZerosDateFormat}
                value={new Date(row.shiftDetail.time.start)}
                disableOpenPicker
                onChange={() => {}}
                slotProps={{
                  textField: { fullWidth: true, size: "small", name: "date" },
                }}
              />
            </Box>
          </OrderFormShortRowGridItem>
          <OrderFormShortRowGridItem item xs={2} width="160px">
            <OrderTimeField
              id={row.id}
              value={time}
              disabled={
                isReadOnly ||
                isAutomatchingOn ||
                !isExpertAuthorizedToEdit ||
                !!talent
              }
              validationErrors={validationErrors}
              onChange={(value: string) => handleTimeChange(row.id, value)}
            />
          </OrderFormShortRowGridItem>
          <OrderFormShortRowGridItem item xs={3} width="200px">
            <Autocomplete
              value={custCC}
              disabled={isReadOnly || !isExpertAuthorizedToEdit || !!talent}
              sx={{ backgroundColor: "rgba(249, 249, 250, 0.6)" }}
              options={custCCOptions}
              getOptionLabel={(option) => option.name}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              onChange={(_, newValue: AutocompleteType | null) =>
                handleCustomerCCChange(row.orderRowId, row.id, newValue)
              }
              renderInput={(params) => (
                <TextFieldLight
                  {...params}
                  size="small"
                  name="customerCostCenter"
                />
              )}
            />
          </OrderFormShortRowGridItem>
          <OrderFormShortRowGridItem item xs={5}>
            {talent ? (
              <ShiftTalentInfo
                talent={talent}
                rowId={row.orderRowId}
                assignmentId={talent.assignmentId}
                cancelAssignment={cancelAssignment}
                canCancelAllShifts={canCancelAllShifts}
                cancelAllAssignments={cancelAllAssignments}
              />
            ) : null}
          </OrderFormShortRowGridItem>
        </Grid>
        <Grid item container xs={12} spacing={1} mt="-10px">
          {!isBundle && !talentAgreedFlexibleStartTime && (
            <>
              <OrderFormShortRowGridItem item xs={2}>
                <FormControlLabel
                  disabled={isFlexShiftDisabled}
                  sx={{ marginLeft: 0, marginRight: 0 }}
                  onChange={() => {
                    const isFlexibleValue = !isStartFlexible;
                    setIsStartFlexible(isFlexibleValue);
                    // Clear value when unchecking
                    if (!isFlexibleValue) {
                      handleFlexibleStartTimeChange(row.id, "");
                    }
                  }}
                  control={
                    <Checkbox
                      sx={{ padding: "9px 4px 9px 0px" }}
                      disableRipple
                      checked={isStartFlexible}
                    />
                  }
                  labelPlacement="end"
                  label={
                    <Typography
                      color={isFlexShiftDisabled ? "text.disabled" : "inherit"}
                    >
                      {t("startIsFlexible")}
                    </Typography>
                  }
                />
              </OrderFormShortRowGridItem>
              <OrderFormShortRowGridItem item xs={2} width="160px">
                <Box display="flex" alignItems="center">
                  {talentAgreedFlexibleStartTime && (
                    <Tooltip
                      title={`${t("talentAgreedToStartAt")} ${format(
                        new Date(talentAgreedFlexibleStartTime),
                        hoursMinutesFormat,
                      )}`}
                    >
                      <InfoOutlinedIcon
                        sx={{ marginRight: "6px" }}
                        color="primary"
                      />
                    </Tooltip>
                  )}
                  {!talentAgreedFlexibleStartTime && (
                    <OrderFlexibleStartField
                      error={!isFlexStartValid.isValid}
                      value={flexibleStartTimeValue}
                      disabled={isFlexShiftDisabled || !isStartFlexible}
                      onChange={(value: string | undefined) => {
                        handleFlexibleStartTimeChange(row.id, value || "");
                      }}
                    />
                  )}
                </Box>
              </OrderFormShortRowGridItem>
            </>
          )}
          <OrderFormShortRowGridItem item xs={7}>
            <FormControlLabel
              sx={{ marginLeft: 0, marginRight: 0 }}
              onChange={() => {
                handleFillShiftChange(row.id, !row.shiftDetail.isFillShift);
              }}
              control={
                <Checkbox
                  disabled
                  sx={{ padding: "9px 4px 9px 0px" }}
                  disableRipple
                  checked={row.shiftDetail.isFillShift}
                />
              }
              labelPlacement="end"
              label={
                <Typography color="text.disabled">{t("fillShift")}</Typography>
              }
            />
          </OrderFormShortRowGridItem>
        </Grid>
      </Grid>
    </Grid>
  );
};
